import type { CampaignInterface } from '@/interfaces/Campaign.interface';

export type CampaignViewDetails = {
  launchedLabel: string;
  launchedAtFormatted: string;
  launchedByLabel: string | null;
  showPauseAction: boolean;
  showResumeAction: boolean;
  showEditAction: boolean;
  editActionLabel: 'Edit';
  showDuplicateAction: boolean;
};

const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('en', {
  numeric: 'always'
});

const RELATIVE_INTERVALS = [
  { unit: 'year' as const, seconds: 31536000 },
  { unit: 'month' as const, seconds: 2592000 },
  { unit: 'week' as const, seconds: 604800 },
  { unit: 'day' as const, seconds: 86400 },
  { unit: 'hour' as const, seconds: 3600 },
  { unit: 'minute' as const, seconds: 60 }
];

export function resolveCampaignDisplayTimezone(
  campaign: CampaignInterface
): string {
  return campaign.timezone?.trim() || 'Europe/London';
}

export function formatCampaignLaunchedRelativeTime(
  launchedAt: Date | string | null | undefined,
  now = new Date()
): string {
  if (!launchedAt) {
    return '';
  }

  const date = launchedAt instanceof Date ? launchedAt : new Date(launchedAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);

  if (Math.abs(diffSeconds) < 60) {
    return RELATIVE_TIME_FORMATTER.format(diffSeconds, 'second');
  }

  for (const { unit, seconds } of RELATIVE_INTERVALS) {
    const value = Math.round(diffSeconds / seconds);
    if (Math.abs(value) >= 1) {
      return RELATIVE_TIME_FORMATTER.format(value, unit);
    }
  }

  return RELATIVE_TIME_FORMATTER.format(diffSeconds, 'second');
}

function formatCampaignDateTimeParts(date: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'shortOffset'
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  const datePart = `${get('day')}/${get('month')}/${get('year')}`;
  const timePart = `${get('hour')}:${get('minute')}`;
  const timezonePart = get('timeZoneName');

  return `${datePart}, ${timePart}${timezonePart ? ` ${timezonePart}` : ''}`;
}

function resolveShortTimezoneOffset(date: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    timeZoneName: 'shortOffset'
  });

  return (
    formatter
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}

export function formatCampaignLaunchedAt(
  launchedAt: Date | string | null | undefined,
  timeZone = 'Europe/London'
): string {
  if (!launchedAt) {
    return '—';
  }

  const date = launchedAt instanceof Date ? launchedAt : new Date(launchedAt);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return formatCampaignDateTimeParts(date, timeZone);
}

export function formatCampaignScheduledStartAt(
  startDate: string | null | undefined,
  sendTime: string | null | undefined,
  timeZone = 'Europe/London'
): string {
  if (!startDate?.trim()) {
    return '—';
  }

  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate.trim());
  if (!dateMatch) {
    return '—';
  }

  const [, year, month, day] = dateMatch;
  const datePart = `${day}/${month}/${year}`;
  const referenceDate = new Date(`${startDate.trim()}T12:00:00Z`);
  if (Number.isNaN(referenceDate.getTime())) {
    return '—';
  }

  const timezonePart = resolveShortTimezoneOffset(referenceDate, timeZone);
  const trimmedSendTime = sendTime?.trim();

  if (!trimmedSendTime) {
    return timezonePart ? `${datePart} ${timezonePart}` : datePart;
  }

  return `${datePart}, ${trimmedSendTime}${timezonePart ? ` ${timezonePart}` : ''}`;
}

function formatCampaignActorBy(
  actor: CampaignInterface['launched_by_user']
): string | null {
  if (!actor) {
    return null;
  }

  return actor.full_name?.trim() || actor.email?.trim() || null;
}

export function formatCampaignLaunchedBy(
  campaign: CampaignInterface
): string | null {
  return formatCampaignActorBy(campaign.launched_by_user);
}

export function formatCampaignPausedBy(
  campaign: CampaignInterface
): string | null {
  return formatCampaignActorBy(campaign.paused_by_user);
}

export function formatCampaignPausedAt(campaign: CampaignInterface): string {
  return formatCampaignLaunchedAt(
    campaign.paused_at,
    resolveCampaignDisplayTimezone(campaign)
  );
}

function parseShortOffsetMinutes(offset: string): number | null {
  const match = /^(?:GMT|UTC)(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(
    offset.trim()
  );
  if (!match) {
    return null;
  }

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);

  return sign * (hours * 60 + minutes);
}

function resolveZonedWallClockDate(
  startDate: string,
  sendTime: string,
  timeZone: string
): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate.trim());
  const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(sendTime.trim());
  if (!dateMatch || !timeMatch) {
    return null;
  }

  const [, year, month, day] = dateMatch;
  const [, hour, minute, second] = timeMatch;
  const naiveUtcMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? 0)
  );
  if (Number.isNaN(naiveUtcMs)) {
    return null;
  }

  let offsetMinutes: number | null;
  try {
    offsetMinutes = parseShortOffsetMinutes(
      resolveShortTimezoneOffset(new Date(naiveUtcMs), timeZone)
    );
  } catch {
    return null;
  }
  if (offsetMinutes === null) {
    return null;
  }

  return new Date(naiveUtcMs - offsetMinutes * 60000);
}

function resolveCampaignLaunchDate(campaign: CampaignInterface): Date | null {
  if (campaign.launched_at) {
    const date = new Date(campaign.launched_at);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (!campaign.start_date) {
    return null;
  }

  // start_date/send_time are wall-clock values in the campaign timezone,
  // so they must be resolved against it rather than the browser timezone.
  return resolveZonedWallClockDate(
    campaign.start_date,
    campaign.send_time || '00:00',
    resolveCampaignDisplayTimezone(campaign)
  );
}

function formatScheduledMetadataLine(campaign: CampaignInterface): string {
  if (!campaign.start_date) {
    return 'Scheduled to launch';
  }

  const scheduledAt = formatCampaignScheduledStartAt(
    campaign.start_date,
    campaign.send_time,
    resolveCampaignDisplayTimezone(campaign)
  );

  return `Scheduled to launch • ${scheduledAt}`;
}

export function formatCampaignMetadataLine(
  campaign: CampaignInterface
): string {
  if (campaign.status === 'draft') {
    return formatCampaignLastEdited(campaign.updated_at);
  }

  if (campaign.status === 'scheduled') {
    return formatScheduledMetadataLine(campaign);
  }

  const launchDate = resolveCampaignLaunchDate(campaign);
  const timeZone = resolveCampaignDisplayTimezone(campaign);
  const launchedBy = formatCampaignLaunchedBy(campaign);

  let line = 'Launched';

  if (launchDate) {
    const relative = formatCampaignLaunchedRelativeTime(launchDate);
    if (relative) {
      line += ` ${relative}`;
    }
  }

  if (launchedBy) {
    line += ` by ${launchedBy}`;
  }

  const absolute = launchDate
    ? formatCampaignLaunchedAt(launchDate, timeZone)
    : '—';

  if (absolute !== '—') {
    line += ` • ${absolute}`;
  }

  return line;
}

function buildLaunchedMeta(campaign: CampaignInterface) {
  if (campaign.status === 'draft') {
    return {
      launchedLabel: 'Not launched yet',
      launchedAtFormatted: '—'
    };
  }

  if (campaign.status === 'scheduled' && campaign.start_date) {
    return {
      launchedLabel: 'Scheduled to launch',
      launchedAtFormatted: formatCampaignScheduledStartAt(
        campaign.start_date,
        campaign.send_time,
        resolveCampaignDisplayTimezone(campaign)
      )
    };
  }

  const timeZone = resolveCampaignDisplayTimezone(campaign);

  if (campaign.launched_at) {
    return {
      launchedLabel: 'Launched',
      launchedAtFormatted: formatCampaignLaunchedAt(
        campaign.launched_at,
        timeZone
      )
    };
  }

  if (campaign.start_date) {
    return {
      launchedLabel: 'Launched',
      launchedAtFormatted: formatCampaignScheduledStartAt(
        campaign.start_date,
        campaign.send_time,
        timeZone
      )
    };
  }

  return {
    launchedLabel: 'Launched',
    launchedAtFormatted: '—'
  };
}

export function getCampaignViewDetails(
  campaign: CampaignInterface
): CampaignViewDetails {
  const launchedMeta = buildLaunchedMeta(campaign);

  return {
    launchedLabel: launchedMeta.launchedLabel,
    launchedAtFormatted: launchedMeta.launchedAtFormatted,
    launchedByLabel: formatCampaignLaunchedBy(campaign),
    showPauseAction:
      campaign.status === 'active' ||
      campaign.status === 'scheduled' ||
      campaign.status === 'pending',
    showResumeAction: campaign.status === 'paused',
    showEditAction: campaign.status === 'paused',
    editActionLabel: 'Edit',
    showDuplicateAction: true
  };
}

export function getCampaignHeaderLabel(campaign: CampaignInterface): string {
  if (campaign.campaign_type?.name) {
    return campaign.campaign_type.name.toUpperCase();
  }
  return 'CAMPAIGN';
}

export function formatCampaignLastEdited(
  updatedAt: string | null | undefined
): string {
  if (!updatedAt) {
    return 'Last edited — • Auto-saved';
  }

  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) {
    return 'Last edited — • Auto-saved';
  }

  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const prefix = isToday
    ? `Last edited today at ${timePart}`
    : `Last edited ${date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })} at ${timePart}`;

  return `${prefix} • Auto-saved`;
}
